import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { EyeOff, Paperclip, Pencil, Trash2, X } from 'lucide-react';
import { S3Avatar, S3Badge, S3EmptyState } from './primitives';
import { splitMentions, threadItemVisual, type S3ThreadItem } from './threadModel';
import { formatDate } from '../constants';
import type { Attachment } from '../../../services/supportApi';
import { useSupportLook } from '../v4/lookContext';
import ConfirmDialog from '../../ConfirmDialog';
import DraftReplyCard, { type DraftActions } from './DraftReplyCard';
import { Linkified } from '../linkify';
import '../../../styles/support-note-actions.css';

const NOTE_LABEL = 'Nota interna · visibile solo al gruppo';

/** `@Nome` menzionato nella veste v3 (support-v3.css non si tocca): token info. */
const MENTION_V3_STYLE: React.CSSProperties = {
  color: 'var(--a2-info-fg)', background: 'var(--a2-info-bg)', borderRadius: 4, padding: '0 3px', fontWeight: 600,
};

interface ThreadPanelProps {
  items: S3ThreadItem[];
  publicOnly: boolean;
  onTogglePublicOnly: () => void;
  /** Note nascoste dal filtro: alimenta la pastiglia del bottone. */
  noteCount: number;
  /** Senza `supporttickets.handle` le note non esistono per l'utente. */
  canSeeNotes: boolean;
  onOpenAttachment: (a: Attachment) => void;
  /**
   * Identita del thread mostrato (l'id del ticket). Cambiarla azzera lo
   * scroll: passando da un ticket all'altro nello split-view il pannello non
   * si smonta, e senza questo segnale resterebbe alla posizione precedente.
   */
  threadKey?: string;
  /** Il composer e il piede della stessa card (prototipo L255). */
  composer?: React.ReactNode;
  /**
   * Modifica dei messaggi del RICHIEDENTE (permesso *.edit_requester_messages).
   * Chi ha solo `handle` non modifica nulla, nemmeno i propri messaggi.
   */
  canEditRequesterMessages?: boolean;
  /** Modifica dei messaggi del CANALE, compresi i propri (*.edit_channel_messages). */
  canEditChannelMessages?: boolean;
  /** Ticket chiuso: niente matita (il backend risponderebbe 409). */
  closed?: boolean;
  /**
   * Salvataggio della correzione. Deve RIGETTARE in caso di errore: la bolla
   * resta in modifica con la bozza dell'operatore, e il contenitore (che
   * possiede il toast e il refetch) avvisa e ricarica.
   */
  onEditMessage?: (messageId: string, body: string) => Promise<void>;
  /**
   * Rimozione di un allegato da un messaggio modificabile (stessi permessi
   * della correzione del testo). `index` e la posizione ORIGINALE
   * dell'allegato nel messaggio. Deve rigettare in caso di errore: toast e
   * refetch li fa il contenitore.
   */
  onRemoveAttachment?: (messageId: string, index: number) => Promise<void>;
  /**
   * Risposte in bozza (Task 46): azioni sulle card `draft`. Assente = le card
   * compaiono in sola lettura (nessun bottone).
   */
  drafts?: DraftActions;
  /**
   * Note interne (24/10/2026): correzione del testo. Matita solo sulle note con
   * `canEditNote` (lo decide il backend: permesso *.edit_internal_notes o autore
   * entro 15 minuti). Deve RIGETTARE in caso di errore, come `onEditMessage`.
   */
  onEditNote?: (noteId: string, body: string) => Promise<void>;
  /** Eliminazione (logica) della nota, dopo conferma. Rigetta in caso di errore. */
  onDeleteNote?: (noteId: string) => Promise<void>;
}

const NO_DRAFT_ACTIONS: DraftActions = {
  canApprove: false,
  onApprove: () => Promise.resolve(),
  onReject: () => Promise.resolve(),
  onEdit: () => Promise.resolve(),
  onDelete: () => Promise.resolve(),
};

/** Contenitore del chip allegato quando accanto c'e la × di rimozione. */
const ATTACH_WRAP_STYLE: React.CSSProperties = {
  alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: '100%', minWidth: 0,
};
const ATTACH_REMOVE_STYLE: React.CSSProperties = { width: 22, height: 22, minHeight: 22, flex: 'none' };

/** Quanto lontano dal fondo si considera ancora "in fondo" (arrotondamenti dello scroll). */
const AT_BOTTOM_PX = 48;

/**
 * Modifica di un messaggio: la textarea prende TUTTA la larghezza della colonna
 * conversazione (non quella della bolla) ed e ridimensionabile nei due sensi.
 * Inline e non in `support-v3.css` perche vale identica nelle due vesti e
 * deve vincere sulla larghezza della bolla (`.s3-item__col`) e su quella della
 * riga di modifica v4 (`.sup4 .s3-item__edit`). I colori restano dei token.
 */
/** Corpo che il backend salva per una nota di soli allegati (addNote). */
const NOTE_ATTACHMENT_PLACEHOLDER = '(allegato)';
const EDIT_MIN_HEIGHT = 160;
const NOTE_LOCKED_STYLE: React.CSSProperties = { margin: '4px 0 0', whiteSpace: 'normal' };
/** Altezza iniziale massima, in frazione della finestra (~60vh). */
const EDIT_MAX_VH = 0.6;
const EDIT_COL_STYLE: React.CSSProperties = { flex: '1 1 auto', maxWidth: '100%' };
const EDIT_BOX_STYLE: React.CSSProperties = { width: '100%' };
const EDIT_TA_STYLE: React.CSSProperties = {
  width: '100%', maxWidth: '100%', minHeight: EDIT_MIN_HEIGHT, resize: 'both',
};

/**
 * Card "Conversazione" (prototipo L227-L252, SPEC §3.10): thread unico con le
 * note interne inline fra i messaggi (ruling 5). La card NON fa fetch e non
 * conosce il ticket: riceve gia le voci fuse da `mergeThread` e delega
 * l'apertura degli allegati al contenitore (download via blob autenticato).
 */
const ThreadPanel: React.FC<ThreadPanelProps> = ({
  items, publicOnly, onTogglePublicOnly, noteCount, canSeeNotes, onOpenAttachment, threadKey, composer,
  canEditRequesterMessages = false, canEditChannelMessages = false, closed = false, onEditMessage,
  onRemoveAttachment,
  drafts,
  onEditNote,
  onDeleteNote,
}) => {
  const v4 = useSupportLook() === 'v4';
  const listRef = useRef<HTMLDivElement>(null);
  // Le note vanno via per due motivi indipendenti, e il componente li applica
  // entrambi invece di fidarsi di come il chiamante ha chiamato `mergeThread`:
  // il permesso (`canSeeNotes`) e il filtro di lettura (`publicOnly`). Senza
  // `supporttickets.handle` una nota non raggiunge mai il DOM, qualunque cosa
  // arrivi in `items` (parita con `TicketDetail.tsx:696`, colonna nascosta).
  // Le bozze (Task 46) seguono il permesso come le note, ma restano col filtro
  // «Solo messaggi pubblici»: sono risposte in attesa, cose da fare.
  const visible = items.filter((i) =>
    i.kind === 'note' ? canSeeNotes && !publicOnly : i.kind === 'draft' ? canSeeNotes : true
  );
  /** Vero finche l'utente non ha scorso all'insu: decide se seguire il thread. */
  const stick = useRef(true);
  /** Bolla in modifica (chiave della voce) con la bozza corrente. */
  // Chiave = _id del messaggio, non la `key` posizionale: se il thread si
  // rilegge e gli indici scalano, la bozza resta sulla bolla giusta.
  // `initial` (solo note): il testo con cui si e aperta la casella, se diverso
  // dal corpo salvato (nota di soli allegati, nota arrivata per email).
  // `note`: la casella e di una nota (serve a capire se e stata eliminata).
  const [editing, setEditing] = useState<{ id: string; body: string; initial?: string; note?: boolean } | null>(null);
  const [saving, setSaving] = useState(false);
  const editRef = useRef<HTMLTextAreaElement>(null);
  const editingId = editing?.id;
  const editingNote = Boolean(editing?.note);

  // Altezza iniziale proporzionata al testo: da EDIT_MIN_HEIGHT fino a ~60vh,
  // poi scorre (e l'operatore puo comunque trascinare la maniglia).
  useLayoutEffect(() => {
    const ta = editRef.current;
    if (!editingId || !ta) return;
    const cap = Math.round(window.innerHeight * EDIT_MAX_VH);
    ta.style.height = `${Math.max(EDIT_MIN_HEIGHT, Math.min(ta.scrollHeight + 2, cap))}px`;
  }, [editingId]);

  /**
   * La matita dipende dal LATO del messaggio: le bolle "in" sono del richiedente,
   * le "out" del canale (compresi i propri messaggi), le note non si modificano.
   * Serve anche l'id del sottodocumento: senza, il backend non saprebbe quale
   * messaggio correggere.
   */
  const canEdit = (item: S3ThreadItem): boolean => {
    if (!onEditMessage || closed || !item.id) return false;
    if (item.kind === 'in') return canEditRequesterMessages;
    if (item.kind === 'out') return canEditChannelMessages;
    return false;
  };

  /** Allegato in attesa di conferma della rimozione. */
  const [removing, setRemoving] = useState<{ messageId: string; index: number; filename: string } | null>(null);
  const [removeBusy, setRemoveBusy] = useState(false);

  /** La × segue la matita: stesso lato, stesso permesso, ticket non chiuso. */
  const canRemove = (item: S3ThreadItem): boolean => {
    if (!onRemoveAttachment || closed || !item.id) return false;
    if (item.kind === 'in') return canEditRequesterMessages;
    if (item.kind === 'out') return canEditChannelMessages;
    return false;
  };

  const confirmRemove = async () => {
    if (!removing || !onRemoveAttachment) return;
    setRemoveBusy(true);
    try {
      await onRemoveAttachment(removing.messageId, removing.index);
    } catch {
      // Avviso e refetch li fa il contenitore.
    } finally {
      setRemoveBusy(false);
      setRemoving(null);
    }
  };

  /**
   * Note interne (24/10/2026): matita e cestino solo dove il backend lo dice
   * (`canEditNote`/`canDeleteNote`), mai su ticket chiuso, e non piu dopo la
   * fine della finestra dell'autore (`editableUntil`, se c'e: oltre, il backend
   * risponderebbe 403). Valutata al render, senza timer.
   */
  const noteWindowOpen = (item: S3ThreadItem): boolean =>
    !item.editableUntil || new Date(item.editableUntil).getTime() > Date.now();
  const canEditNote = (item: S3ThreadItem): boolean =>
    item.kind === 'note' && Boolean(item.canEditNote) && Boolean(onEditNote) && !closed &&
    Boolean(item.id) && noteWindowOpen(item);
  const canDeleteNote = (item: S3ThreadItem): boolean =>
    item.kind === 'note' && Boolean(item.canDeleteNote) && Boolean(onDeleteNote) && !closed &&
    Boolean(item.id) && noteWindowOpen(item);

  /**
   * Apre la modifica di una nota col testo che si vede a schermo: niente
   * segnaposto «(allegato)» (la nota di soli allegati parte vuota: si puo
   * aggiungere del testo, e vuota non si salva) e, per le note arrivate per
   * email, le righe gia riunite come nella bolla (`displayBody`).
   */
  const startNoteEdit = (item: S3ThreadItem) => {
    const initial = item.body === NOTE_ATTACHMENT_PLACEHOLDER ? '' : item.displayBody ?? item.body;
    setEditing({ id: item.id!, body: initial, note: true, ...(initial !== item.body ? { initial } : {}) });
  };

  /**
   * Dopo una rilettura la nota in modifica puo essere cambiata sotto:
   * - eliminata (da un altro: sparisce dal thread) o ticket chiuso: la casella
   *   si chiude da sola;
   *   il fuoco va alla conversazione solo se era nella casella;
   * - non piu modificabile (finestra dei 15 minuti scaduta, permesso tolto, 403
   *   al salvataggio): la casella RESTA col testo scritto, in sola lettura, con
   *   «Salva» disabilitato e un avviso (`noteEditLocked`); chiude «Annulla».
   *   Cosi il testo non sparisce mai senza un messaggio.
   */
  useEffect(() => {
    if (!editingId) return;
    const it = items.find((i) => i.id === editingId);
    // Nota non piu nel thread (eliminata): col filtro «solo pubblici» le note
    // mancano da `items` per scelta, e la casella resta.
    const noteGone = !it && editingNote && !publicOnly;
    if (noteGone || (it && it.kind === 'note' && closed)) {
      const hadFocus = Boolean(editRef.current && editRef.current === document.activeElement);
      setEditing(null);
      if (hadFocus) listRef.current?.focus();
    }
  }, [items, editingId, editingNote, publicOnly, closed]);

  /** Nota in modifica che il server non lascia piu modificare (vedi sopra). */
  const noteEditLocked = (item: S3ThreadItem): boolean =>
    item.kind === 'note' && !item.canEditNote;

  /** Nota in attesa di conferma dell'eliminazione. */
  const [deletingNote, setDeletingNote] = useState<string | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const confirmDeleteNote = async () => {
    if (!deletingNote || !onDeleteNote) return;
    setDeleteBusy(true);
    try {
      await onDeleteNote(deletingNote);
    } catch {
      // Avviso e refetch li fa il contenitore.
    } finally {
      setDeleteBusy(false);
      setDeletingNote(null);
      // La nota non ha piu le sue icone: il fuoco torna alla conversazione.
      listRef.current?.focus();
    }
  };

  /**
   * Fuoco di ritorno alla matita della nota dopo «Annulla»/«Salva»: senza, il
   * fuoco cadrebbe sul body quando la textarea si smonta.
   */
  const focusBackTo = useRef<string | null>(null);
  useEffect(() => {
    if (editingId || !focusBackTo.current) return;
    const id = focusBackTo.current;
    focusBackTo.current = null;
    const btn = listRef.current?.querySelector<HTMLButtonElement>(`[data-note-edit="${id}"]`);
    if (btn) btn.focus();
  }, [editingId]);

  /** Chiude la modifica; sulle note il fuoco torna alla matita. */
  const closeEdit = (item: S3ThreadItem) => {
    if (item.kind === 'note' && item.id) focusBackTo.current = item.id;
    setEditing(null);
  };

  const saveEdit = async (item: S3ThreadItem) => {
    const save = item.kind === 'note' ? onEditNote : onEditMessage;
    if (!editing || !save || !item.id) return;
    // Nota non piu modificabile: il backend risponderebbe 403 (anche Ctrl+Invio).
    if (noteEditLocked(item)) return;
    const body = editing.body.trim();
    if (!body) return;
    // Testo invariato: si chiude senza chiamare il backend (che risponderebbe
    // 200 no-op) e senza far ricaricare il ticket per nulla. Vale anche per il
    // testo di partenza della nota (email riunita): salvarlo cambierebbe il corpo.
    if (body === item.body || (editing.initial !== undefined && body === editing.initial.trim())) {
      closeEdit(item);
      return;
    }
    setSaving(true);
    try {
      await save(item.id, body);
      closeEdit(item);
    } catch {
      // Avviso e refetch li fa il contenitore: qui la bozza resta a schermo
      // perche l'operatore possa correggerla o annullare.
    } finally {
      setSaving(false);
    }
  };

  // Apertura e cambio ticket: si parte dal fondo, cioe dal messaggio piu recente.
  useLayoutEffect(() => {
    stick.current = true;
    // Il ticket cambia: la bozza apparteneva a un messaggio che non e piu a schermo.
    setEditing(null);
    const node = listRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [threadKey]);

  // Arrivo di un nuovo elemento: si segue solo chi stava gia guardando il
  // fondo, altrimenti si strapperebbe la lettura di un messaggio piu vecchio.
  useEffect(() => {
    const node = listRef.current;
    if (node && stick.current) node.scrollTop = node.scrollHeight;
  }, [visible.length]);

  const onScroll = () => {
    const node = listRef.current;
    if (!node) return;
    stick.current = node.scrollHeight - node.scrollTop - node.clientHeight <= AT_BOTTOM_PX;
  };

  const hiddenLabel = noteCount === 1 ? '1 nota nascosta' : `${noteCount} note nascoste`;
  const showHidden = canSeeNotes && publicOnly && noteCount > 0;
  const noteLabel = noteCount === 1 ? '1 nota interna' : `${noteCount} note interne`;
  // Comincia con l'etichetta visibile (WCAG 2.5.3): chi comanda a voce dice
  // quello che legge. Il numero della pastiglia arriva subito dopo.
  const toggleLabel = `Solo messaggi pubblici — ${publicOnly ? hiddenLabel : noteLabel}`;

  return (
    <div className="s3-card s3-thread">
      {/* Intestazione (L229-L232) */}
      <div className="s3-thread__head">
        <span className="s3-h3 s3-thread__title">Conversazione</span>

        {canSeeNotes && (
          <span className="s3-thread__legend">
            <span className="s3-thread__dot" aria-hidden="true" />
            Note interne visibili solo al gruppo
          </span>
        )}

        {/* Regione viva sempre montata: se comparisse solo all'occorrenza, il
            lettore di schermo non annuncerebbe il primo cambiamento. */}
        <span className="s3-meta" role="status">{showHidden ? hiddenLabel : ''}</span>

        {canSeeNotes && noteCount > 0 && (
          <button
            type="button"
            className={`s3-pill s3-pill--sm${publicOnly ? ' s3-pill--active' : ''}`}
            aria-pressed={publicOnly}
            aria-label={toggleLabel}
            onClick={onTogglePublicOnly}
          >
            <EyeOff size={12} aria-hidden="true" /> Solo messaggi pubblici
            {/* Il numero e gia dentro l'aria-label del bottone: ripeterlo
                lo farebbe leggere due volte. */}
            <span className="s3-segcount" aria-hidden="true">{noteCount}</span>
          </button>
        )}
      </div>

      {/* Lista scorrevole (L232-L250). E una regione a se: scorre, quindi deve
          poter ricevere il fuoco e la rotellina da tastiera. */}
      <div
        ref={listRef}
        className="s3-thread__list"
        onScroll={onScroll}
        tabIndex={0}
        role="region"
        aria-label="Conversazione"
      >
        {visible.length === 0 && <S3EmptyState>Nessun messaggio.</S3EmptyState>}
        {visible.length > 0 && (
          <ul className="s3-thread__items">
            {visible.map((item) => {
              if (item.kind === 'draft' && item.draft) {
                return (
                  <li key={item.key} className="s3-item">
                    <DraftReplyCard
                      item={item}
                      closed={closed}
                      onOpenAttachment={onOpenAttachment}
                      {...(drafts || NO_DRAFT_ACTIONS)}
                    />
                  </li>
                );
              }
              const v = threadItemVisual(item.kind);
              const isNote = item.kind === 'note';
              const editingThis = Boolean(editing && item.id && editing.id === item.id);
              return (
                <li key={item.key} className="s3-item" style={{ flexDirection: v.rowDirection }}>
                  {/* La foto resta anche in v4 (CT-75): il prototipo ha solo le iniziali (L418). */}
                  <S3Avatar initials={item.initials} size={v4 ? 32 : 30} tone={v.avatarTone} title={item.authorName} src={item.photo} />
                  <div
                    className="s3-item__col"
                    style={editing && item.id && editing.id === item.id ? EDIT_COL_STYLE : undefined}
                  >
                    <div className="s3-item__meta" style={{ flexDirection: v.rowDirection }}>
                      <span className="s3-item__who">{item.authorName}</span>
                      {item.badge && (
                        <S3Badge tone={isNote ? 'warn' : 'green'} small title={isNote ? NOTE_LABEL : undefined}>
                          {item.badge}
                        </S3Badge>
                      )}
                      {/* La sola bolla ambra non parla: il lettore di schermo
                          deve sapere che la voce non e visibile al richiedente. */}
                      {isNote && <span className="s3-sr">{NOTE_LABEL}</span>}
                      {isNote && item.isNew && (v4 ? (
                        // v4 (L423): chip pieno ambra, testo scuro per il contrasto AA.
                        <span className="s3-badge s3-badge--sm s4-newchip" title="Nota interna non ancora letta da te">
                          Nuova
                        </span>
                      ) : (
                        <S3Badge tone="warn" small title="Nota interna non ancora letta da te">
                          Nuova
                        </S3Badge>
                      ))}
                      {isNote && item.mentionsMe && (v4 ? (
                        <span className="s3-badge s3-badge--sm s4-mentionchip" title="Sei menzionato in questa nota">
                          Ti ha menzionato
                        </span>
                      ) : (
                        <S3Badge tone="info" small title="Sei menzionato in questa nota">
                          Ti ha menzionato
                        </S3Badge>
                      ))}
                      <span className="s3-meta">{formatDate(item.sentAt)}</span>
                      {item.editedAt && !isNote && (
                        <span
                          className="s3-meta s3-item__edited"
                          title={item.editedByName ? `Modificato da ${item.editedByName}` : undefined}
                        >
                          modificato · {formatDate(item.editedAt)}
                        </span>
                      )}
                      {item.editedAt && isNote && (
                        <span className="s3-meta s3-item__edited">
                          modificata{item.editedByName ? ` da ${item.editedByName}` : ''} · {formatDate(item.editedAt)}
                        </span>
                      )}
                      {item.approvedByName && (
                        // Task 46: risposta nata come bozza (solo vista operatore).
                        <span className="s3-meta s3-item__edited" title="Risposta in bozza approvata prima dell'invio">
                          approvata da {item.approvedByName}
                        </span>
                      )}
                      {canEdit(item) && !(editing && item.id && editing.id === item.id) && (
                        <button
                          type="button"
                          className="s3-pill s3-pill--sm s3-pill--icon"
                          aria-label="Modifica messaggio"
                          title="Modifica messaggio"
                          onClick={() => setEditing({ id: item.id!, body: item.body })}
                        >
                          <Pencil size={11} aria-hidden="true" />
                        </button>
                      )}
                      {(canEditNote(item) || canDeleteNote(item)) && !editingThis && (
                        // Visibili al passaggio o al fuoco della nota, sempre su
                        // touch (support-note-actions.css).
                        <span className="s3-note-actions">
                          {canEditNote(item) && (
                            <button
                              type="button"
                              className="s3-pill s3-pill--sm s3-pill--icon"
                              aria-label="Modifica nota"
                              title="Modifica nota"
                              data-note-edit={item.id}
                              onClick={() => startNoteEdit(item)}
                            >
                              <Pencil size={11} aria-hidden="true" />
                            </button>
                          )}
                          {canDeleteNote(item) && (
                            <button
                              type="button"
                              className="s3-pill s3-pill--sm s3-pill--icon"
                              aria-label="Elimina nota"
                              title="Elimina nota"
                              disabled={deleteBusy}
                              onClick={() => setDeletingNote(item.id!)}
                            >
                              <Trash2 size={11} aria-hidden="true" />
                            </button>
                          )}
                        </span>
                      )}
                    </div>
                    {editing && item.id && editing.id === item.id ? (
                      <div className="s3-item__edit" style={EDIT_BOX_STYLE}>
                        <textarea
                          ref={editRef}
                          className="s3-ta"
                          style={EDIT_TA_STYLE}
                          aria-label={isNote ? 'Testo della nota' : 'Testo del messaggio'}
                          value={editing.body}
                          disabled={saving}
                          readOnly={noteEditLocked(item)}
                          aria-describedby={noteEditLocked(item) ? `note-locked-${item.id}` : undefined}
                          autoFocus
                          onChange={(e) => setEditing({ ...editing, id: item.id!, body: e.target.value })}
                          onKeyDown={(e) => {
                            // Ctrl+Invio salva, Esc annulla: le stesse scorciatoie del composer.
                            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                              e.preventDefault();
                              void saveEdit(item);
                            } else if (e.key === 'Escape') {
                              e.preventDefault();
                              closeEdit(item);
                            }
                          }}
                        />
                        {noteEditLocked(item) && (
                          // Rilettura dopo la scadenza o 403 al salvataggio: il
                          // testo resta per poterlo copiare, il salvataggio no.
                          <p id={`note-locked-${item.id}`} role="status" className="s3-meta" style={NOTE_LOCKED_STYLE}>
                            Non puoi più modificare questa nota: sono passati i 15 minuti o non hai più il permesso. Copia il testo se ti serve, poi premi «Annulla».
                          </p>
                        )}
                        <div className="s3-item__edit-actions">
                          {v4 && (
                            // Promemoria delle scorciatoie (L436), solo grafico.
                            <span className="s3-meta s4-item__edit-hint" aria-hidden="true">
                              CTRL+↵ SALVA · ESC ANNULLA
                            </span>
                          )}
                          <button
                            type="button"
                            className="s3-pill s3-pill--sm"
                            disabled={saving}
                            onClick={() => closeEdit(item)}
                          >
                            Annulla
                          </button>
                          <button
                            type="button"
                            className="s3-pill s3-pill--sm s3-pill--primary"
                            disabled={saving || !editing.body.trim() || noteEditLocked(item)}
                            onClick={() => void saveEdit(item)}
                          >
                            Salva
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className={`${v.bubbleClass}${isNote && item.isNew ? ' s3-bubble--note-new' : ''}`}>
                        {isNote && item.mentionNames && item.mentionNames.length
                          ? splitMentions(item.displayBody ?? item.body, item.mentionNames).map((s, si) =>
                              s.mention ? (
                                <span
                                  key={si}
                                  className={v4 ? 's4-mention' : undefined}
                                  style={v4 ? undefined : MENTION_V3_STYLE}
                                  title="Menzionato"
                                >
                                  {s.text}
                                </span>
                              ) : (
                                <Linkified key={si} text={s.text} />
                              )
                            )
                          : <Linkified text={item.displayBody ?? item.body} />}
                      </div>
                    )}
                    {/* Anche gli allegati di una nota nuova portano l'evidenza:
                        in una nota di soli allegati sono il contenuto vero. */}
                    {item.attachments.map((a, i) => {
                      const chip = (
                        <button
                          key={`${a.url}-${i}`}
                          type="button"
                          className={`s3-attach${isNote && item.isNew ? ' s3-attach--note-new' : ''}`}
                          onClick={() => onOpenAttachment(a)}
                        >
                          <Paperclip size={11} aria-hidden="true" />
                          <span className="s3-ellipsis">{a.filename}</span>
                        </button>
                      );
                      if (!canRemove(item)) return chip;
                      // Posizione ORIGINALE (il backend la manda: i rimossi escono
                      // dall'elenco, quindi `i` puo non coincidere).
                      const index = typeof a.index === 'number' ? a.index : i;
                      return (
                        <span key={`${a.url}-${i}`} className="s3-attach-wrap" style={ATTACH_WRAP_STYLE}>
                          {chip}
                          <button
                            type="button"
                            className="s3-pill s3-pill--sm s3-pill--icon"
                            style={ATTACH_REMOVE_STYLE}
                            aria-label={`Rimuovi allegato ${a.filename}`}
                            title="Rimuovi allegato"
                            disabled={removeBusy}
                            onClick={() => setRemoving({ messageId: item.id!, index, filename: a.filename })}
                          >
                            <X size={11} aria-hidden="true" />
                          </button>
                        </span>
                      );
                    })}
                    {/* Allegati rimossi (27/10/2026): spariscono senza segnaposto;
                        chi ha tolto cosa resta nella cronologia del ticket. */}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {composer}

      <ConfirmDialog
        isOpen={removing !== null}
        onClose={() => { if (!removeBusy) setRemoving(null); }}
        onConfirm={() => void confirmRemove()}
        title="Rimuovi allegato"
        message={`Rimuovere l'allegato "${removing?.filename ?? ''}"? L'operazione è definitiva e non avvisa nessuno.`}
        confirmText="Rimuovi"
        type="danger"
        confirmDisabled={removeBusy}
      />

      <ConfirmDialog
        isOpen={deletingNote !== null}
        onClose={() => { if (!deleteBusy) setDeletingNote(null); }}
        onConfirm={() => void confirmDeleteNote()}
        title="Elimina nota"
        message="Eliminare la nota interna? Testo e allegati spariranno dalla conversazione. Nessuno viene avvisato."
        confirmText="Elimina"
        type="danger"
        confirmDisabled={deleteBusy}
      />
    </div>
  );
};

export default ThreadPanel;
